import React, { createContext, useContext, useState, ReactNode } from 'react';
import { DetectedProject } from '../../../../common/utils/projectDetection';
import {
  ProjectCardSize,
  DEFAULT_PROJECT_CARD_SIZE,
} from '../constants/cardSizes';

interface ProjectsContextType {
  // Upload type selection dialog
  uploadTypeDialogOpen: boolean;
  setUploadTypeDialogOpen: (open: boolean) => void;

  // Batch upload state
  batchUploadDialogOpen: boolean;
  setBatchUploadDialogOpen: (open: boolean) => void;
  detectedProjects: DetectedProject[];
  setDetectedProjects: (projects: DetectedProject[]) => void;
  uploadFiles: FileList | null;
  setUploadFiles: (files: FileList | null) => void;
  rootDirHandle: FileSystemDirectoryHandle | null;
  setRootDirHandle: (handle: FileSystemDirectoryHandle | null) => void;
  rootPath: string | undefined;
  setRootPath: (path: string | undefined) => void;
  isBatchUploading: boolean;
  setIsBatchUploading: (uploading: boolean) => void;
  batchUploadProgress: { [projectName: string]: number };
  setBatchUploadProgress: (
    progress:
      | { [projectName: string]: number }
      | ((prev: { [projectName: string]: number }) => {
          [projectName: string]: number;
        })
  ) => void;
  isScanning: boolean;
  setIsScanning: (scanning: boolean) => void;
  currentScanningFile: string | null;
  setCurrentScanningFile: (file: string | null) => void;

  // UI preferences
  cardSize: ProjectCardSize;
  setCardSize: (size: ProjectCardSize) => void;
}

const ProjectsContext = createContext<ProjectsContextType | undefined>(
  undefined
);

export const useProjectsContext = () => {
  const context = useContext(ProjectsContext);
  if (context === undefined) {
    throw new Error(
      'useProjectsContext must be used within a ProjectsProvider'
    );
  }
  return context;
};

interface ProjectsProviderProps {
  children: ReactNode;
}

export const ProjectsProvider: React.FC<ProjectsProviderProps> = ({
  children,
}) => {
  const [uploadTypeDialogOpen, setUploadTypeDialogOpen] = useState(false);
  const [batchUploadDialogOpen, setBatchUploadDialogOpen] = useState(false);
  const [detectedProjects, setDetectedProjects] = useState<DetectedProject[]>(
    []
  );
  const [uploadFiles, setUploadFiles] = useState<FileList | null>(null);
  const [rootDirHandle, setRootDirHandle] =
    useState<FileSystemDirectoryHandle | null>(null);
  const [rootPath, setRootPath] = useState<string | undefined>(undefined);
  const [isBatchUploading, setIsBatchUploading] = useState(false);
  const [batchUploadProgress, setBatchUploadProgress] = useState<{
    [projectName: string]: number;
  }>({});
  const [isScanning, setIsScanning] = useState(false);
  const [currentScanningFile, setCurrentScanningFile] = useState<string | null>(
    null
  );
  const [cardSize, setCardSize] = useState<ProjectCardSize>(
    DEFAULT_PROJECT_CARD_SIZE
  );

  const value: ProjectsContextType = {
    uploadTypeDialogOpen,
    setUploadTypeDialogOpen,
    batchUploadDialogOpen,
    setBatchUploadDialogOpen,
    detectedProjects,
    setDetectedProjects,
    uploadFiles,
    setUploadFiles,
    rootDirHandle,
    setRootDirHandle,
    rootPath,
    setRootPath,
    isBatchUploading,
    setIsBatchUploading,
    batchUploadProgress,
    setBatchUploadProgress,
    isScanning,
    setIsScanning,
    currentScanningFile,
    setCurrentScanningFile,
    cardSize,
    setCardSize,
  };

  return (
    <ProjectsContext.Provider value={value}>
      {children}
    </ProjectsContext.Provider>
  );
};
